// React Query integration for offline-resilient reads (offline-resilience substrate).
//
// useCachedQuery() wraps useQuery (v5 object form) so that:
//   • on a successful queryFn result it write-throughs to persistentCache (last-known-good);
//   • it seeds placeholderData from the synchronous cache mirror (getCachedSync) so cached data
//     renders instantly and survives a cold offline start;
//   • it returns the normal query plus { isStale, cachedAt, isOffline } so callers can show a
//     "showing saved data" affordance.
//
// Additive only — existing useQuery call sites are untouched. The global QueryClient is configured
// with networkMode:'offlineFirst' (see App.tsx) so the queryFn still runs while offline and the
// cached placeholder shows immediately if it rejects.

import { useEffect, useRef } from 'react';
import {
  useQuery,
  type QueryKey,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import { getCachedSync, setCached, type CachedEnvelope } from '@/lib/offline/persistentCache';
import { getIsOnline } from '@/hooks/useIsOnline';

export interface UseCachedQueryOptions<TData>
  extends Omit<UseQueryOptions<TData, Error, TData, QueryKey>, 'queryKey' | 'queryFn' | 'placeholderData'> {
  /** React Query key. */
  queryKey: QueryKey;
  /** The fetcher; its resolved value is written through to the persistent cache. */
  queryFn: () => Promise<TData>;
  /** Stable string key under which last-known-good data is persisted. */
  cacheKey: string;
}

export interface CachedQueryExtras {
  /**
   * True when the data currently shown came from the persistent cache rather than a fresh fetch.
   * (Named `isFromCache` rather than `isStale` to avoid shadowing React Query's own
   * time-based `UseQueryResult.isStale`, which keeps its native meaning on the returned object.)
   */
  isFromCache: boolean;
  /** ISO timestamp the served-from-cache value was written, or null when fresh/never-cached. */
  cachedAt: string | null;
  /** Synchronous reachability (false === offline). */
  isOffline: boolean;
}

export type UseCachedQueryResult<TData> = UseQueryResult<TData, Error> & CachedQueryExtras;

/**
 * useQuery wrapper with persistent-cache write-through + synchronous offline placeholder.
 */
export function useCachedQuery<TData>(
  opts: UseCachedQueryOptions<TData>,
): UseCachedQueryResult<TData> {
  const { queryKey, queryFn, cacheKey, ...rest } = opts;

  // Read the cached envelope once per render synchronously for the placeholder + cachedAt.
  const placeholderEnvelope = getCachedSync<TData>(cacheKey);

  // Synchronous placeholder so cached data paints before/without a network round-trip. Keep the
  // last placeholder if a fresh read fails (function form), otherwise seed from the cache.
  // React Query types the function form's return against `NonFunctionGuard<TData>` (it strips
  // function-typed data); our cached values are always plain JSON, never functions, so casting to
  // the option's own placeholderData type is sound. The cast is localized because NonFunctionGuard
  // is not part of the public type surface.
  const placeholderData = ((prev: TData | undefined) =>
    prev ?? placeholderEnvelope?.data) as UseQueryOptions<
    TData,
    Error,
    TData,
    QueryKey
  >['placeholderData'];

  const query = useQuery<TData, Error, TData, QueryKey>({
    queryKey,
    queryFn,
    placeholderData,
    ...rest,
  });

  // Write-through on every fresh success. Track the last persisted reference so we don't re-serialize
  // an unchanged payload (and so placeholder data, which equals the cached value, isn't re-written).
  const lastWritten = useRef<TData | undefined>(undefined);
  const { data, isSuccess, isPlaceholderData } = query;
  useEffect(() => {
    if (isSuccess && !isPlaceholderData && data !== undefined && data !== lastWritten.current) {
      lastWritten.current = data;
      void setCached(cacheKey, data);
    }
  }, [isSuccess, isPlaceholderData, data, cacheKey]);

  // Served-from-cache when React Query is currently handing back placeholder data (i.e. the real
  // fetch hasn't resolved yet — typically offline or first paint).
  const servedFromCache = query.isPlaceholderData && placeholderEnvelope != null;

  const extras: CachedQueryExtras = {
    isFromCache: servedFromCache,
    cachedAt: servedFromCache ? placeholderEnvelope!.cachedAt : null,
    isOffline: !getIsOnline(),
  };

  return Object.assign(query, extras);
}

export type { CachedEnvelope };
